import type { Side } from './espn'
import { points, type Ruleset } from './scoring'

/*
 * What one cell of the grid says.
 *
 * This is deliberately NOT `Segment` from rank.ts. The race bar answers "how
 * is this player doing", so a blank and a loss are the same nought to it. The
 * grid answers "what did everyone take", where a blank and a loss are entirely
 * different facts about a person, and where a pick we were simply not told
 * about is a third thing again.
 */
export type Cell =
  | 'won' // final, and they called it
  | 'lost' // final, and they did not
  | 'void' // final, but there was no winner to call
  | 'live' // running, with a pick on the table
  | 'open' // not started - a pick, with nothing decided in it yet
  | 'none' // no pick, which is now always a fact rather than a withholding

export interface GridGame {
  /*
   * There is no `locked` here any more. It existed for one job - deciding
   * whether a blank cell meant "no pick" or "not telling you yet" - and since
   * every pick became public the moment it is made, a blank is always a blank.
   */
  /** Whether THIS game has kicked off. */
  started: boolean
  final: boolean
  winnerSide: Side | 'tie' | 'void' | null
}

/**
 * One cell, from one game and one player's pick.
 *
 * `pick` being null used to mean two things - they skipped it, or the server
 * refused to tell us - and `isMine` was how they were told apart. Every pick
 * is public now, so a null is only ever the first, and the cell can say "no
 * pick" about anybody without asserting something it cannot know.
 */
export function cellState(game: GridGame, pick: Side | null): Cell {
  // Nothing has happened in this game yet: either they have picked, or they
  // have not, and both are plainly visible.
  if (!game.started) return pick !== null ? 'open' : 'none'

  if (pick === null) return 'none'

  if (!game.final) return 'live'

  // A tie or a void is not a miss. Depending on the ruleset it may have paid
  // both sides or neither, so it must not be struck through like a wrong call.
  if (game.winnerSide === null || game.winnerSide === 'tie' || game.winnerSide === 'void') {
    return 'void'
  }

  return pick === game.winnerSide ? 'won' : 'lost'
}

export interface Tally {
  /** Games this player got credit for. */
  hits: number
  /** Games that could be got right or wrong at all. */
  decided: number
}

/**
 * A hit rate, for a row of the grid or a column of it.
 *
 * The same function does both, because they are the same question asked along
 * two axes: one player across ten games, or twelve players across one game.
 *
 * WHY IT DEFERS TO `points` RATHER THAN COUNTING GOLD SQUARES. Two rules make
 * the visible cells the wrong thing to add up. Under `missingPick: 'favorite'`
 * a blank IS a pick - the rules assign the favourite - and it scores whenever
 * that favourite wins, so a tally of chosen picks would disagree with the
 * standings the first time anyone forgot a game. And the underdog bonus pays
 * three, so points and hits are different numbers: this asks how OFTEN you
 * were right, which is the only one a percentage can be taken of.
 *
 * Ties and voids are in neither half. Under `tieCredits: 'both'` a tie pays
 * the entire field, and a free hit for everyone moves every percentage at once
 * while saying nothing about how anybody picked.
 */
export function tally(
  games: readonly (GridGame & { favSide: Side | null; spread: number | null })[],
  picks: readonly (Side | null)[],
  rules: Ruleset,
): Tally {
  let hits = 0
  let decided = 0
  games.forEach((g, i) => {
    if (!g.final) return
    if (g.winnerSide === null || g.winnerSide === 'tie' || g.winnerSide === 'void') return
    decided += 1
    const scored = points(
      picks[i] ?? null,
      { favSide: g.favSide, spread: g.spread },
      { winnerSide: g.winnerSide, final: true },
      rules,
    )
    if (scored > 0) hits += 1
  })
  return { hits, decided }
}

/**
 * The rate as a whole number, or null when nothing has been decided.
 *
 * Null rather than zero: "0%" says a player has got everything wrong, and
 * before kickoff that is a claim about them that is not true yet.
 */
export function pct(t: Tally): number | null {
  return t.decided === 0 ? null : Math.round((t.hits / t.decided) * 100)
}
